import AppPopup from '@/components/popups/app-popup';
import BoardUtility from '@/scripts/board-utility';
import EventBus from '@/scripts/event-bus';

export const CustomFieldsServiceMixin = {
    data() {
        return {
            selectedItems: [],
            deletingItems: [],
        };
    },

    methods: {
        createId(prefix) {
            return `${prefix}-${this.$helpers.createGuid()}`;
        },

        refreshEvent() {
            EventBus.$emit('custom-field:update');
        },

        editItem({ id }, config) {
            const widget = {
                name: 'custom-field-widget',
                customFieldId: id,
                options: config,
            };

            EventBus.$emit('open-widget', widget);
        },

        getCheckboxOrDropdownValues(item, initialValues = []) {
            if (item && item.ObjectValueJson.length > 0) {
                const currentValues = JSON.parse(item.ObjectValueJson);

                return initialValues.map((element) => {
                    const values = currentValues.find((value) => element.Name === value.name);

                    return values ? values : { id: element.Id, name: element.Name, selected: false };
                });
            }

            return initialValues.map((item) => {
                return {
                    id: item.Id,
                    name: item.Name,
                    selected: false,
                };
            });
        },

        getDateTimeValue(date) {
            return date ? this.$moment(date).format('MM/DD/YYYY') : null;
        },

        isDateTime(customField) {
            return customField.inputType === 'date picker';
        },

        getUrlValue(url) {
            return url.includes('http') ? url : `http://${url}`;
        },

        isUrl(customField) {
            return customField.inputType === 'url';
        },

        isCheckbox(customField) {
            return customField.inputType.includes('checkbox');
        },

        isDropdown(customField) {
            return customField.inputType.includes('dropdown');
        },

        createDeleteDomainModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Custom Field',
                        action: () => confirmFunction(item),
                    },
                    cancel: {
                        label: 'Cancel',
                    },
                };
                const content =
                    '<p>All information associated with the custom field will be deleted. You can not undo this action.</p>';
                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete this custom field?',
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    }
                );
            };
        },

        asCustomFieldItem(item) {
            return {
                id: item.Id,
                name: item.Name,
                description: item.Description,
                order: item.Order,
                values: item.CustomDataFields,
                inputType: item.InputType,
                updatedDate: this.$moment(item.UpdatedAt),
                createdDate: this.$moment(item.CreatedAt),
            };
        },

        asCustomFieldReadOnlyItem(item = null, customField) {
            const customFieldData = {
                id: customField.id,
                name: customField.name,
                description: customField.description,
                inputType: customField.inputType,
                customDataFields: customField.values.map(({ Id, Name }) => ({
                    id: Id,
                    name: Name,
                    selected: false,
                })),
                value: null,
            }

            if (item) {
                customFieldData.customFieldValueId = item.Id;
            }

            switch (customFieldData.inputType) {
                case 'multi-line text':
                    customFieldData.value = item ? item.LongStringValue : '';
                    break;
                case 'number':
                    customFieldData.value = item ? item.DoubleValue : 0;
                    break;
                case 'date picker':
                    customFieldData.value = item && item.DateTimeValue ? new Date(item.DateTimeValue) : null;
                    break;
                case 'single checkbox':
                case 'multiple checkboxes':
                case 'single dropdown':
                    customFieldData.customDataFields = this.getCheckboxOrDropdownValues(item, customField.values);
                    break;
                default:
                    customFieldData.value = item ? item.ShortStringValue : '';
            }

            return customFieldData;

        },

        asCustomFieldInputItem(item = null, customField) {
            const customFieldInput = {
                id: customField.id,
                name: customField.name,
                description: customField.description,
                inputType: customField.inputType,
                customFieldValue: {
                    name: null,
                    value: null,
                },
            };

            if (item) {
                customFieldInput.customFieldValueId = item.Id;
            }

            switch (customField.inputType) {
                case 'multi-line text':
                    customFieldInput.generalInputType = 'textarea';
                    customFieldInput.customFieldValue.name = 'LongStringValue';
                    customFieldInput.customFieldValue.value = item ? item.LongStringValue : '';
                    break;
                case 'number':
                    customFieldInput.generalInputType = 'number';
                    customFieldInput.customFieldValue.name = 'DoubleValue';
                    customFieldInput.customFieldValue.value = item ? item.DoubleValue : 0;
                    break;
                case 'date picker':
                    customFieldInput.generalInputType = 'date-picker';
                    customFieldInput.customFieldValue.name = 'DateTimeValue';
                    customFieldInput.customFieldValue.value = item && item.DateTimeValue ? new Date(item.DateTimeValue) : null;
                    break;
                case 'single checkbox':
                case 'multiple checkboxes':
                    customFieldInput.generalInputType = 'checkbox';
                    customFieldInput.customFieldValue.name = 'ObjectValueJson';
                    customFieldInput.customFieldValue.value = this.getCheckboxOrDropdownValues(item, customField.values);
                    break;
                case 'single dropdown':
                    customFieldInput.generalInputType = 'dropdown';
                    customFieldInput.customFieldValue.name = 'ObjectValueJson';
                    customFieldInput.customFieldValue.value = this.getCheckboxOrDropdownValues(item, customField.values);
                    break;
                default:
                    customFieldInput.generalInputType = 'input';
                    customFieldInput.customFieldValue.name = 'ShortStringValue';
                    customFieldInput.customFieldValue.value = item ? item.ShortStringValue : '';
            }

            return customFieldInput;
        },

        getFilterParam(options) {
            const queryFilters = [];

            if (options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }

                if (options.search) {
                    queryFilters.push(this.getSearchFilter(options.search));
                }
            }

            return queryFilters.length > 0 ? `where=[${queryFilters.join(', ')}]` : '';
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);

                if (filters.operation === 'Or') {
                    return `{ "$or": [${viewQueryFilters.join(', ')}]}`;
                } else {
                    return `{ "$and": [${viewQueryFilters.join(', ')}]}`;
                }
            }
            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'name':
                    return BoardUtility.getCustomFieldNameFilterQuery(filter);
                case 'description':
                    return BoardUtility.getCustomFieldDescriptionFilterQuery(filter);
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);
            return `{"Name": {"$like": "%${encodedSearch}%"}}`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page ? `offset=${options.itemsPerPage * (options.page - 1)}` : '';
        },

        getTopParam(options) {
            return options.itemsPerPage ? `limit=${options.itemsPerPage}` : '';
        },

        getCustomFieldsQueryParams(options) {
            return `?${[this.getFilterParam(options), this.getPageParam(options), this.getTopParam(options)]
                .filter(Boolean)
                .join('&')}`;
        },

        getCustomFieldValuesQueryParams(options = {}, customFieldId = null) {
            const queries = [];

            if (options.type === 'Contact') {
                queries.push(`{"ContactId": {"$eq": ${options.contactId}}}`);

                if (customFieldId) {
                    queries.push(`{"CustomContactFieldId": {"$eq": ${customFieldId}}}`);
                }
            } else if (options.type === 'Opportunity') {
                queries.push(`{"OpportunityAccountId": {"$eq": ${options.opportunityId}}}`);

                if (customFieldId) {
                    queries.push(`{"CustomOpportunityFieldId": {"$eq": ${customFieldId}}}`);
                }
            } else {
                queries.push(`{"CompanyId": {"$eq": ${options.companyId}}}`);

                if (customFieldId) {
                    queries.push(`{"CustomCompanyFieldId": {"$eq": ${customFieldId}}}`);
                }
            }

            return `?where=[${queries.join(', ')}]`;
        },

        deleteItem(item, config) {
            this.deletingItems = [item];
            this.$api
                .delete(this.getDeleteServiceUrl(config, item))
                .then(() => {
                    this.$helpers.showSuccessNotification(`Custom Field: ${item.name}, deleted successfully`);
                    this.deletingItems = [];
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        `An error occurred trying to delete the Custom Field: '${item.Name}'`,
                        { error }
                    );
                });
        },

        getCustomFields(options = {}, config) {
            var service = `/ms/custom-fields/v1/custom${config.type}Fields/${config.clientId}`;

            if (config.type === 'AgencyClient') {
                service = `/ms/custom-fields/v1/custom${config.type}Fields/account/${config.agencyAccountId}`
            }

            const query = this.getCustomFieldsQueryParams(options);
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`${service}${query}`)
                    .then(({ data }) => {
                        resolve({
                            items: data.data.map(this.asCustomFieldItem),
                            count: data.count,
                        });
                    })
                    .catch(reject);
            });
        },

        getCustomFieldValue(config, customField, readOnlyRender) {
            const service = `/ms/custom-fields/v1/custom${config.type}FieldValues/${config.clientId}`;
            const query = this.getCustomFieldValuesQueryParams(config, customField.id);

            return new Promise((resolve, reject) => {
                this.$api
                    .get(`${service}${query}`)
                    .then(({ data }) => {
                        if (readOnlyRender) {
                            resolve(this.asCustomFieldReadOnlyItem(data.data[0], customField))
                        } else {
                            resolve(this.asCustomFieldInputItem(data.data[0], customField));
                        }
                    })
                    .catch(reject);
            });
        },

        getDeleteServiceUrl(config, item) {
            if (config.type === 'AgencyClient') {
                return `/ms/custom-fields/v1/custom${config.type}Fields/${item.id}/account/${config.agencyAccountId}`;
            } else {
                return `/ms/custom-fields/v1/custom${config.type}Fields/${item.id}/${config.clientId}`;
            }
        },
    },
};
